import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router';

import { ApiClientError } from '@/api/client';
import { fetchMe } from '@/api/endpoints';
import { queryKeys } from '@/api/queryClient';
import { NoAccessPage } from '@/components/layout/NoAccessPage';
import { PageLoader } from '@/components/ui';
import { Shell } from '@/components/layout/Shell';
import { DashboardPage } from '@/modules/dashboard/DashboardPage';
import { accessStateAtom, authUserAtom, currentProjectAtom } from '@/store';

/**
 * Every screen except the dashboard is split out (spec §9.2).
 *
 * The dashboard is what a session starts on, so it stays in the entry chunk; the editor drags in the
 * virtualizer and the admin screens are opened by a handful of people, and neither belongs in the
 * bundle everybody downloads to look at coverage bars.
 *
 * AI jobs is switched off until an AI provider is configured: its module stays in the tree,
 * but no route or nav entry leads to it. To bring it back, restore its lazy import and `<Route>`
 * here, its entry in `Shell.tsx` NAV and its screen key in `help/screens.ts`.
 */
const ActivityPage = lazy(async () => ({
  default: (await import('@/modules/activity/ActivityPage')).ActivityPage,
}));
const GlossaryPage = lazy(async () => ({
  default: (await import('@/modules/glossary/GlossaryPage')).GlossaryPage,
}));
const ImportsPage = lazy(async () => ({
  default: (await import('@/modules/imports/ImportsPage')).ImportsPage,
}));
const KeysPage = lazy(async () => ({
  default: (await import('@/modules/keys/KeysPage')).KeysPage,
}));
const LinkedKeysPage = lazy(async () => ({
  default: (await import('@/modules/key-links/LinkedKeysPage')).LinkedKeysPage,
}));
const MissingKeysPage = lazy(async () => ({
  default: (await import('@/modules/missing-keys/MissingKeysPage')).MissingKeysPage,
}));
const PackageSettingsPage = lazy(async () => ({
  default: (await import('@/modules/packages/PackageSettingsPage')).PackageSettingsPage,
}));
const PackagesListPage = lazy(async () => ({
  default: (await import('@/modules/packages/PackagesListPage')).PackagesListPage,
}));
const SuggestionsPage = lazy(async () => ({
  default: (await import('@/modules/packages/SuggestionsPage')).SuggestionsPage,
}));
const ProjectSettingsPage = lazy(async () => ({
  default: (await import('@/modules/projects/ProjectSettingsPage')).ProjectSettingsPage,
}));
const ProjectsListPage = lazy(async () => ({
  default: (await import('@/modules/projects/ProjectsListPage')).ProjectsListPage,
}));
const SearchPage = lazy(async () => ({
  default: (await import('@/modules/search/SearchPage')).SearchPage,
}));
const ReleasesPage = lazy(async () => ({
  default: (await import('@/modules/releases/ReleasesPage')).ReleasesPage,
}));
const ApiTokensPage = lazy(async () => ({
  default: (await import('@/modules/settings/ApiTokensPage')).ApiTokensPage,
}));
const LocalesPage = lazy(async () => ({
  default: (await import('@/modules/settings/LocalesPage')).LocalesPage,
}));
const PublishTargetsPage = lazy(async () => ({
  default: (await import('@/modules/settings/PublishTargetsPage')).PublishTargetsPage,
}));
const PhraseSyncPage = lazy(async () => ({
  default: (await import('@/modules/settings/PhraseSyncPage')).PhraseSyncPage,
}));
const UsersPage = lazy(async () => ({
  default: (await import('@/modules/settings/UsersPage')).UsersPage,
}));

/**
 * Routes and the admission gate (spec §9.2).
 *
 * Nothing renders until `/access/me` has answered: the registration gate has no exemptions, so an
 * unregistered user must see the no-access page rather than a half-loaded editor whose every
 * request fails with 403.
 */
export function App({
  account,
  onSignOut,
}: {
  account: { name: string | null; username: string | null } | null;
  onSignOut: () => void;
}) {
  const setAuthUser = useSetAtom(authUserAtom);
  const setAccessState = useSetAtom(accessStateAtom);
  const accessState = useAtomValue(accessStateAtom);

  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: fetchMe,
    retry: false,
    meta: { silent: true },
  });

  useEffect(() => {
    if (me.isPending) return;
    if (me.data) {
      setAuthUser(me.data);
      setAccessState('ready');
      return;
    }
    const error = me.error;
    if (error instanceof ApiClientError) {
      setAccessState(error.isNotRegistered ? 'not-registered' : 'error');
      return;
    }
    setAccessState('error');
  }, [me.isPending, me.data, me.error, setAuthUser, setAccessState]);

  if (accessState === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}>
        <PageLoader label="Signing in" />
      </Box>
    );
  }

  if (accessState === 'not-registered') {
    return <NoAccessPage email={account?.username} onSignOut={onSignOut} />;
  }

  if (accessState === 'error') {
    return (
      <NoAccessPage
        email={account?.username}
        reason={
          me.error instanceof ApiClientError
            ? `${me.error.code}: ${me.error.message}`
            : 'The Glossa API did not answer.'
        }
        onSignOut={onSignOut}
      />
    );
  }

  return (
    <Shell onSignOut={onSignOut}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/projects" element={<ProjectsListPage />} />
          <Route path="/projects/:code" element={<ProjectRedirect />} />
          <Route
            path="/projects/:code/keys"
            element={
              <ProjectScope>
                <KeysPage />
              </ProjectScope>
            }
          />
          <Route
            path="/projects/:code/linked-keys"
            element={
              <ProjectScope>
                <LinkedKeysPage />
              </ProjectScope>
            }
          />
          <Route
            path="/projects/:code/suggestions"
            element={
              <ProjectScope>
                <SuggestionsPage scopeType="project" />
              </ProjectScope>
            }
          />
          <Route
            path="/projects/:code/settings"
            element={
              <ProjectScope>
                <ProjectSettingsPage />
              </ProjectScope>
            }
          />
          <Route
            path="/projects/:code/releases"
            element={
              <ProjectScope>
                <ReleasesPage />
              </ProjectScope>
            }
          />
          <Route
            path="/projects/:code/missing"
            element={
              <ProjectScope>
                <MissingKeysPage />
              </ProjectScope>
            }
          />
          <Route
            path="/projects/:code/imports"
            element={
              <ProjectScope>
                <ImportsPage />
              </ProjectScope>
            }
          />
          <Route
            path="/projects/:code/activity"
            element={
              <ProjectScope>
                <ActivityPage />
              </ProjectScope>
            }
          />
          <Route path="/packages" element={<PackagesListPage />} />
          <Route path="/packages/:code" element={<Navigate to="settings" replace />} />
          <Route path="/packages/:code/keys" element={<KeysPage scopeType="package" />} />
          <Route path="/packages/:code/imports" element={<ImportsPage scopeType="package" />} />
          <Route path="/packages/:code/settings" element={<PackageSettingsPage />} />
          <Route path="/packages/:code/suggestions" element={<SuggestionsPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/settings/users" element={<UsersPage />} />
          <Route path="/settings/api-tokens" element={<ApiTokensPage />} />
          <Route path="/settings/locales" element={<LocalesPage />} />
          <Route path="/settings/publish-targets" element={<PublishTargetsPage />} />
          <Route path="/settings/phrase-sync" element={<PhraseSyncPage />} />
          <Route path="/keys" element={<Navigate to="/projects" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Shell>
  );
}

/** Shown while a route chunk downloads — the halo, not a blank frame. */
function RouteFallback() {
  return (
    <PageLoader />
  );
}

/** Keeps the switcher in step with the URL — the URL is the source of truth (spec §9.4). */
function ProjectScope({ children }: { children: React.ReactNode }) {
  const { code } = useParams();
  const setCurrentProject = useSetAtom(currentProjectAtom);

  useEffect(() => {
    if (code) setCurrentProject(code);
  }, [code, setCurrentProject]);

  return <>{children}</>;
}

function ProjectRedirect() {
  const { code } = useParams();
  return <Navigate to={`/projects/${code}/keys`} replace />;
}

function NotFound() {
  return (
    <Box sx={{ py: 8, textAlign: 'center' }}>
      <Typography variant="h1" sx={{ mb: 1 }}>
        Not found
      </Typography>
      <Typography sx={{ color: 'text.secondary' }}>
        This screen has not been built yet, or the address is wrong.
      </Typography>
    </Box>
  );
}
